/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
import type { Dispatch, SetStateAction } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { generateUUID } from "@swiggy-private/dls-web-recipe-common-web/dist/components/Customise/helpers";
import type { CartItemVariant } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";

import { QrCodeActions, ToastType } from "@swiggy-private/portal-dineout-context";
import { ICartItems, ILocalCart, IQrCodeAction } from "@swiggy-private/portal-dineout-context";
import type {
    CartItem,
    CartItemAddonGroup,
    CartItemAddonWithName,
    CartItemFlatAddon,
    UpdateCartWithActionOptions,
} from "types/cart";
import { showToast } from "./helper";
import { sendCustomNREvWithHeaders } from "./newrelic";
import { IInstructionAction } from "./types";
import { SPECIAL_INSTRUCTIONS } from "../constants/cart";
import { STORAGE_KEYS } from "../constants/StorageKeys";

// eslint-disable-next-line no-undef
let debounceTimeout: string | number | NodeJS.Timeout | undefined;

const debounce = (func: (...args: any[]) => Promise<void>, delay: number) => {
    return function (...args: any[]) {
        return new Promise<void>((resolve) => {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(async () => {
                await func(...args);
                resolve();
            }, delay);
        });
    };
};

const toCartItemVariant = (groupId: string, variationId: string, name = ""): CartItemVariant => ({
    group_id: groupId,
    variation_id: variationId,
    variationId,
    name,
});

export const isFlatAddonList = (addons: CartItem["addons"] | undefined): boolean => {
    const first = addons?.[0] as Record<string, unknown> | undefined;
    return Boolean(first && (first.choice_id != null || first.choiceId != null) && !Array.isArray(first.choices));
};

const flatAddonsToCartItemAddons = (addons: CartItemFlatAddon[]): CartItemAddonWithName[] =>
    addons
        .map((addon) => {
            const choiceId = String(addon.choice_id ?? addon.choiceId ?? "");
            const groupId = String(addon.group_id ?? addon.groupId ?? "");
            return { choice_id: choiceId, choiceId, group_id: groupId, groupId, name: addon.name ?? "" };
        })
        .filter((addon) => addon.choice_id && addon.group_id);

export const extractSelectedAddons = (cartLine: CartItem): CartItemAddonWithName[] => {
    const rawAddons = cartLine.selectedInfo?.addons ?? cartLine.addons;
    if (isEmpty(rawAddons)) {
        return [];
    }

    if (isFlatAddonList(rawAddons)) {
        return flatAddonsToCartItemAddons(rawAddons as CartItemFlatAddon[]);
    }

    return (rawAddons as CartItemAddonGroup[])
        .flatMap((addon) =>
            (addon.choices ?? []).map((choice) => {
                const choiceId = String(choice.id ?? "");
                const groupId = String(addon.groupId ?? "");
                return { choice_id: choiceId, choiceId, group_id: groupId, groupId, name: choice.name ?? "" };
            }),
        )
        .filter((addon) => addon.choice_id && addon.group_id);
};

export const extractSelectedVariants = (cartLine: CartItem): CartItemVariant[] => {
    const variantGroups = cartLine.selectedInfo?.variantsV2?.variantGroups ?? cartLine.variantsV2?.variantGroups;
    if (!isEmpty(variantGroups)) {
        return variantGroups
            .flatMap((group) => {
                const groupId = String(group.groupId ?? "");
                return (group.variations ?? []).map((variation) =>
                    toCartItemVariant(groupId, String(variation.id ?? ""), variation.name ?? ""),
                );
            })
            .filter((variant) => variant.group_id && variant.variation_id);
    }

    return (cartLine.variants ?? [])
        .map((variant) =>
            toCartItemVariant(String(variant.group_id ?? ""), String(variant.variation_id ?? ""), variant.name ?? ""),
        )
        .filter((variant) => variant.group_id && variant.variation_id);
};

const transformCartItems = (cartItems: CartItem[]) => {
    return cartItems.map((cartLine) => {
        const selectedAddons = extractSelectedAddons(cartLine);
        const selectedVariants = extractSelectedVariants(cartLine);

        return {
            menu_item_id: cartLine.info?.id,
            name: cartLine.name ?? cartLine.info?.name ?? "",
            menu_item_uuid: generateUUID(cartLine.info?.id, selectedAddons, selectedVariants),
            addons: selectedAddons.map(({ choice_id, group_id, name }) => ({
                choice_id: String(choice_id),
                group_id: String(group_id),
                name: name || "",
            })),
            variants: selectedVariants.map(({ group_id, variation_id, name }) => ({
                group_id: String(group_id),
                variation_id: String(variation_id),
                name: name || "",
            })),
            quantity: cartLine.selectedInfo?.quantity?.value,
            instructions: cartLine.item_instructions,
        };
    });
};

export const updateCartWithAction = async (options: UpdateCartWithActionOptions) => {
    const { updateLogic, helperFn, updatedDishItems, updateLocalCartInNative, clearLocalCartInNative } = options;
    const { setIsCartEmpty, setUpdatingItemId, updateCart, setShowOverlay, isCartUpdating, callbacks } = helperFn;
    let finalLocalCart: ILocalCart = {
        cartItems: [],
        restaurantId: "",
        tableId: "",
        specialInstructions: "",
    };
    try {
        const localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}");
        finalLocalCart = localCart || { cartItems: [], restaurantId: "", tableId: "", specialInstructions: "" };
    } catch (_) {
        sendCustomNREvWithHeaders("session_storage_local_cart_error", {});
    }

    if (!finalLocalCart || !finalLocalCart.cartItems || isCartUpdating) {
        return;
    }

    const updatedCartItems = updateLogic(updatedDishItems);
    const tranformedCartItems = transformCartItems(updatedCartItems);
    // Debounce the updateCart function
    const debouncedUpdateCart = debounce(updateCart, 300);

    const updatedCart = {
        ...finalLocalCart,
        cartItems: tranformedCartItems,
    };

    const isCartEmpty = isEmpty(tranformedCartItems);

    try {
        if (isCartEmpty) {
            setIsCartEmpty(true);
        }
        setShowOverlay(true);
        await debouncedUpdateCart(updatedCart);

        // Call native bridge callbacks after successful update
        if (isCartEmpty) {
            clearLocalCartInNative?.();
        } else {
            updateLocalCartInNative?.(updatedCart as unknown as ILocalCart);
        }

        callbacks?.onSuccess?.();
    } catch (_) {
        callbacks?.onError?.();
    } finally {
        setShowOverlay(false);
        if (setUpdatingItemId) setUpdatingItemId("");
    }
};
interface UpdateLocalCartParams {
    localCart: ILocalCart;
    updatedCartItems: ICartItems[];
    restaurantId: string | undefined;
    tableId: string | undefined;
    parentOrderId?: string;
}

interface ManageSpecialInstructionsParams {
    instructionAction: IInstructionAction;
    dispatch: Dispatch<IQrCodeAction>;
    updateCart: (payload: Record<string, unknown>) => Promise<void>;
    updateLocalCartInNative?: (cartData: ILocalCart) => void;
    setShowOverlay: Dispatch<SetStateAction<boolean>>;
    isCartUpdating: boolean;
}

export const updateLocalCart = (
    { localCart, updatedCartItems, restaurantId, tableId, parentOrderId }: UpdateLocalCartParams,
    dispatch: Dispatch<IQrCodeAction>,
) => {
    const updatedPayload: ILocalCart = {
        ...localCart,
        cartItems: updatedCartItems,
        restaurantId: restaurantId || "",
        tableId: tableId || "",
        ...(localCart.specialInstructions && { specialInstructions: localCart.specialInstructions }),
        parentOrderId: parentOrderId || "",
    };
    dispatch({
        type: QrCodeActions.UPDATE_LOCAL_CART,
        payload: updatedPayload,
    });
    try {
        sessionStorage.setItem(STORAGE_KEYS.LOCAL_CART, JSON.stringify(updatedPayload));
    } catch (_) {}
};

// TODO: to combine move this in updateCartWithAction and remove this function. Single function for updating cart
export const manageSpecialInstructions = async ({
    instructionAction,
    dispatch,
    updateCart,
    updateLocalCartInNative,
    isCartUpdating,
    setShowOverlay,
}: ManageSpecialInstructionsParams) => {
    let finalLocalCart: ILocalCart = {
        cartItems: [],
        restaurantId: "",
        tableId: "",
        specialInstructions: "",
    };
    try {
        const localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}");
        if (!isEmpty(instructionAction.specialInstructions) && instructionAction.action !== "remove") {
            finalLocalCart = {
                ...localCart,
                specialInstructions: instructionAction.specialInstructions?.trim(),
            };
        } else {
            finalLocalCart = {
                ...localCart,
                specialInstructions: "",
            };
        }
    } catch (_) {
        sendCustomNREvWithHeaders("session_storage_local_cart_error", {});
    }

    if (!finalLocalCart || !finalLocalCart.cartItems || isCartUpdating) {
        return;
    }

    const debouncedUpdateCart = debounce(updateCart, 0);
    try {
        setShowOverlay(true);
        await debouncedUpdateCart(finalLocalCart, 0);
        updateLocalCart(
            {
                localCart: finalLocalCart,
                updatedCartItems: finalLocalCart.cartItems,
                restaurantId: finalLocalCart.restaurantId,
                tableId: finalLocalCart.tableId,
            },
            dispatch,
        );

        // Update native cart after successful update
        updateLocalCartInNative?.(finalLocalCart);

        showToast({ dispatch, type: ToastType.SUCCESS, message: instructionAction.message || "" });
    } catch (_) {
        sendCustomNREvWithHeaders("update_local_cart_error", {});
        showToast({ dispatch, type: ToastType.ERROR, message: SPECIAL_INSTRUCTIONS.ERROR_MESSAGE });
    } finally {
        setShowOverlay(false);
    }
};
